import * as THREE from 'three';
import { fbm2 } from '../../core/rng';
import type { BuiltLocation, LocationDef } from '../Location';
import { paintableSnowMaterial, registerPaintable } from '../paintable';
import { bench, building, car, disposeScene, skyDome, streetlight, sun } from '../props';
import { createTerrain } from '../terrain';

/** Snowy street corner at dusk: buildings, parked cars (paintable hoods and
 * roofs), warm streetlights, benches. */
export const cityLocation: LocationDef = {
  id: 'city',
  name: 'City Corner',
  description: 'A plaza at dusk. Paint the street, the benches won’t mind — the car owners might.',
  thumbnail: { skyTop: '#2b3a5e', skyBottom: '#e8926b', ground: '#dfe5ee', accent: '#ffb35c' },

  build(ctx): BuiltLocation {
    const scene = new THREE.Scene();
    const colliders: THREE.Box3[] = [];
    const paintables: BuiltLocation['paintables'] = [];

    scene.fog = new THREE.FogExp2(0x8d7a86, 0.009);
    scene.add(
      skyDome([
        [0, '#232f52'],
        [0.55, '#4a4a74'],
        [0.8, '#a06a78'],
        [1, '#e8926b'],
      ]),
    );

    scene.add(new THREE.HemisphereLight(0x8090b8, 0x9a8578, 0.75));
    scene.add(sun(0xffa068, 0.85, -70, 28, 30, 65));

    const height = (x: number, z: number) => fbm2(x * 0.06, z * 0.06, ctx.seed) * 0.09;
    const snow = paintableSnowMaterial({ grainScale: 1100 });
    const terrain = createTerrain({ size: 120, segments: 90, height, material: snow.material });
    scene.add(terrain.mesh);
    registerPaintable(paintables, 'ground', terrain.mesh, snow.uniform, true);

    // Buildings framing an L-shaped street corner (north and west edges).
    const facades: [number, number, number, number, number, string][] = [
      // w, h, d, x, z, tint
      [18, 34, 14, -24, -46, '#3a4254'],
      [14, 26, 14, -6, -46, '#463f52'],
      [16, 40, 14, 12, -46, '#39465a'],
      [15, 22, 14, 30, -46, '#4a4456'],
      [14, 30, 16, -46, -24, '#3d4356'],
      [14, 24, 15, -46, -6, '#474055'],
      [14, 36, 16, -46, 13, '#3a4b5e'],
      [14, 20, 15, -46, 31, '#453e50'],
    ];
    for (const [w, h, d, x, z, tint] of facades) {
      scene.add(building(w, h, d, x, z, 0, colliders, tint));
    }
    // A couple of distant silhouettes beyond the playable edge.
    scene.add(building(26, 52, 20, 40, -80, 0, null, '#2c3344'));
    scene.add(building(22, 44, 20, -80, 40, 0, null, '#2c3344'));

    // Parked cars with paintable snow on hood + roof.
    const carColors: [string, number, number, number, number][] = [
      ['car-0', 0x8c3532, 18, -32, 0],
      ['car-1', 0x2e4a6b, 26.5, -32, 0],
      ['car-2', 0x6d6f75, -32, 20, Math.PI / 2],
    ];
    for (const [id, color, x, z, ry] of carColors) {
      const result = car(id, color, x, z, height(x, z), ry, colliders, (surfaceId) => {
        const bound = paintableSnowMaterial({ grainScale: 60 });
        return {
          material: bound.material,
          register: (mesh) => registerPaintable(paintables, surfaceId, mesh, bound.uniform, false),
        };
      });
      scene.add(result.group);
    }

    // Streetlights and benches.
    for (const [x, z] of [
      [-14, -34],
      [8, -34],
      [30, -34],
      [-34, -12],
      [-34, 12],
      [12, 16],
    ] as const) {
      scene.add(streetlight(x, z, height(x, z)));
      colliders.push(
        new THREE.Box3(
          new THREE.Vector3(x - 0.2, 0, z - 0.2),
          new THREE.Vector3(x + 0.2, 4.6, z + 0.2),
        ),
      );
    }
    scene.add(bench(-6, -33, height(-6, -33), 0));
    scene.add(bench(-33, 2, height(-33, 2), Math.PI / 2));
    colliders.push(
      new THREE.Box3(new THREE.Vector3(-7, 0, -33.4), new THREE.Vector3(-5, 1, -32.6)),
      new THREE.Box3(new THREE.Vector3(-33.4, 0, 1), new THREE.Vector3(-32.6, 1, 3)),
    );

    return {
      scene,
      spawn: { pos: new THREE.Vector3(4, height(4, 10), 10), yaw: Math.PI * 0.15 },
      paintables,
      colliders,
      groundHeight: height,
      groundSurfaceIdAt: () => 'ground',
      bounds: { minX: -38, maxX: 55, minZ: -38, maxZ: 55 },
      baseFogDensity: 0.009,
      dispose() {
        disposeScene(scene);
      },
    };
  },
};
